import type { ComponentType } from 'react'
import { logger } from '@/lib/logger'

/**
 * The chunk behind each sidebar destination, in one place.
 *
 * App.tsx builds its React.lazy component from these same thunks, so the module
 * path for a route is written once. The sidebar calls preloadRoute() on hover,
 * which starts the same import early; by the time the click lands the chunk is
 * usually already in the module cache.
 *
 * Routes not reachable from the sidebar stay declared in App.tsx.
 */
type ChunkLoader = () => Promise<{ default: ComponentType }>

export const routeChunks = {
  '/dashboard': () => import('@/pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
  // Tenant Contacts hub. A super admin with no active tenant gets a different
  // screen on this path, so preloadRoute skips it when the sidebar is unscoped.
  '/customers': () => import('@/pages/customers/CustomerHubPage').then((m) => ({ default: m.CustomerHubPage })),
  '/customers/search': () => import('@/pages/customers/CustomerSearchPage').then((m) => ({ default: m.CustomerSearchPage })),
  '/customers/lists': () => import('@/pages/customers/CustomerListsPage').then((m) => ({ default: m.CustomerListsPage })),
  '/action-center': () => import('@/pages/action-center/ActionCenterPage').then((m) => ({ default: m.ActionCenterPage })),
  '/collections/pending-payments': () => import('@/pages/collections/PendingPaymentsPage').then((m) => ({ default: m.PendingPaymentsPage })),
  '/risk': () => import('@/pages/risk/RiskDashboardPage').then((m) => ({ default: m.RiskDashboardPage })),
  '/payment-attempts': () => import('@/pages/payments/PaymentAttemptsPage').then((m) => ({ default: m.PaymentAttemptsPage })),
  '/forecast': () => import('@/pages/forecast/CashFlowForecastPage').then((m) => ({ default: m.CashFlowForecastPage })),
  '/reports/deferred-revenue': () => import('@/pages/reports/DeferredRevenuePage').then((m) => ({ default: m.DeferredRevenuePage })),
  '/flows': () => import('@/pages/flows/FlowListPage').then((m) => ({ default: m.FlowListPage })),
  '/pending-tasks': () => import('@/pages/pending-tasks/PendingTasksPage').then((m) => ({ default: m.PendingTasksPage })),
  '/activity-log': () => import('@/pages/activity-log/ActivityLogPage').then((m) => ({ default: m.ActivityLogPage })),
  '/settings': () => import('@/pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
  '/brand-comparison': () => import('@/pages/dashboard/BrandComparisonPage').then((m) => ({ default: m.BrandComparisonPage })),
  '/organizations': () => import('@/pages/super-admin/OrganizationsPage').then((m) => ({ default: m.OrganizationsPage })),
  '/human-tasks': () => import('@/pages/super-admin/SuperAdminHumanTasksPage').then((m) => ({ default: m.SuperAdminHumanTasksPage })),
  '/super-admins': () => import('@/pages/super-admin/SuperAdminManagementPage').then((m) => ({ default: m.SuperAdminManagementPage })),
  '/system-settings': () => import('@/pages/super-admin/SystemSettingsPage').then((m) => ({ default: m.SystemSettingsPage })),
  '/operational-config': () => import('@/pages/super-admin/OperationalConfigPage').then((m) => ({ default: m.OperationalConfigPage })),
  '/operations': () => import('@/pages/super-admin/OperationsPage').then((m) => ({ default: m.OperationsPage })),
} satisfies Record<string, ChunkLoader>

/** Paths whose chunk depends on the viewer's scope, so an unscoped sidebar must not guess. */
const TENANT_SCOPED_ONLY = new Set(['/customers'])

/**
 * Start the import for a sidebar destination.
 *
 * `path` is the href with any /:tenantId prefix already removed. `tenantScoped`
 * says whether that prefix was present. Repeat calls are free: the module cache
 * returns the same promise.
 */
export function preloadRoute(path: string, tenantScoped: boolean): void {
  const key = path.split('?', 1)[0]
  if (!tenantScoped && TENANT_SCOPED_ONLY.has(key)) return
  const load = (routeChunks as Record<string, ChunkLoader | undefined>)[key]
  if (!load) return
  load().catch((error: unknown) => {
    // A prefetch miss costs the user nothing: React.lazy runs the same import
    // again on the real navigation, and the ErrorBoundary owns that failure.
    // Record the reason so a broken chunk is still visible in the console.
    logger.warn('preloadRoute', `prefetch failed for ${key}: ${String(error)}`)
  })
}
